import { FlyToCartLayer } from './FlyToCartLayer'
import { HomePage } from './HomePage'

/**
 * `customer-menu` — what the restaurant sells, and how a customer puts it in a basket.
 *
 * Two paths, one page: legacy ships `/menu` as an alias so the header link works even though the menu is
 * a section of the home page rather than a route of its own. The alias is kept (dropping it breaks that
 * link), and marked, so the route diff counts it as an alias rather than a second page.
 *
 * The home page is assembled from the franchise's own module list — `modules.ts` and `module-content.ts`
 * are the rules for that, and FB-C05 is why an unknown module type is skipped with a note rather than
 * rendered blank.
 */
export const customerMenuModule = {
  name: 'customer-menu',
  routes: [
    { path: '/', page: 'Home', showsFooter: true, component: HomePage },
    { path: '/menu', page: 'Home', alias: true, showsFooter: true, component: HomePage },
  ],
  // Legacy mounts the add-to-cart animation app-wide, and so does this: it watches the basket rather
  // than any one call site, so a route this module does not own still gets it.
  banners: [{ name: 'fly-to-cart', component: FlyToCartLayer }],
} as const

export { HomePage }
export { HomeModuleSection } from './HomeModules'
export {
  KNOWN_MODULES,
  contentList,
  contentText,
  modulesOf,
  renderable,
  franchiseModulesOf,
  unknownNote,
  unrenderable,
  withinTier,
  type HomeModule,
} from './modules'
export {
  aboutUsOf,
  allowedTourUrl,
  bannerOf,
  guaranteesOf,
  missingPickedNote,
  newsletterOf,
  mostOrderedOf,
  pickedProductsOf,
  resolvePicked,
  socialLinksOf,
  testimonialsOf,
  virtualTourOf,
  type AboutUs,
  type BannerContent,
  type GuaranteeBlock,
  type Newsletter,
  type PickedProducts,
  type SocialLinks,
  type Testimonial,
  type VirtualTour,
} from './module-content'
export {
  EMPTY_KOMBO,
  isComplete,
  komboName,
  komboPriceCents,
  komboProblem,
  komboTotalCents,
  selectablePizzas,
  sizesOf,
  type KomboPizza,
  type KomboSize,
  type KomboSlots,
} from '@agos/shared'
export { FlyToCartLayer }
export { RECENT_PRESS_MS, flightFrom, pressTarget, type Press } from './fly-to-cart'
export { nextOpening, todayOpening } from './opening-banner'
export {
  serviceExplanation,
  serviceHeading,
  serviceStateOf,
  type ServiceState,
} from './service-state'
export { BoxBuilder } from './BoxBuilder'
export {
  EMPTY_BOX,
  boxConfigsOf,
  boxFor,
  boxesFor,
  boxGroupId,
  boxLines,
  boxProblem,
  optionsFor,
  type BoxConfig,
  type BoxSelection,
  type BoxSlot,
} from './burger-box'
// §9.142 — the gift rule now lives in `customer-core`, because the checkout offers it too.
// Re-exported so nothing that imported it from here has to change.
export {
  giftLine,
  giftOffers,
  giftRulesOf,
  type CountableLine,
  type GiftOffer,
  type GiftRule,
} from '@agos/customer-core'
export { KomboBuilder } from './KomboBuilder'
export { LunchMenuModal } from './LunchMenuModal'
export {
  lunchSections,
  lunchSettingsOf,
  lunchWeeksOf,
  mealsOn,
  upcomingLunchDays,
  type LunchDayOption,
} from './lunch-days'
